import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { detectLanguageFromPath, getLocalizedRoute, getBaseRoute } from '@/lib/routes';
import { getInternalArticleSlug, getLocalizedArticleSlug } from '@/lib/articleSlugMapping';
import { translations } from './translations';

type Language = 'en' | 'nl';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Detect language from URL first, then fallback to localStorage
  const [language, setLanguage] = useState<Language>(() => {
    // First, try to detect from URL (using window.location since we're in initial state)
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const urlLanguage = detectLanguageFromPath(currentPath);
      if (urlLanguage) {
        return urlLanguage;
      }
    }
    
    // Fallback to localStorage
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
    const savedLanguage = localStorage.getItem('byteet-language');
    // Romanian (ro) has been removed - migrate to English if user had it selected
    const validLanguages: Language[] = ['en', 'nl'];
    if (savedLanguage && validLanguages.includes(savedLanguage as Language)) {
      return savedLanguage as Language;
    }
    // If user had 'ro' selected, clear it and use 'en'
    if (savedLanguage === 'ro') {
      localStorage.setItem('byteet-language', 'en');
    }
    return 'en';
      }
    } catch (error) {
      console.warn('Failed to access localStorage:', error);
    }
    return 'en';
  });
  
  // Sync language with URL when location changes
  useEffect(() => {
    const urlLanguage = detectLanguageFromPath(location.pathname);
    if (urlLanguage !== language) {
      setLanguage(urlLanguage);
      // Update localStorage to match URL
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('byteet-language', urlLanguage);
        }
      } catch (error) {
        console.warn('Failed to save language to localStorage:', error);
      }
    }
    // Keep document lang attribute in sync
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', urlLanguage);
    }
  }, [location.pathname, language]);

  const handleSetLanguage = (lang: Language) => {
    if (lang === language) return; // No change needed
    
    setLanguage(lang);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('byteet-language', lang);
      }
    } catch (error) {
      console.warn('Failed to save language to localStorage:', error);
    }
    
    // Navigate to the localized version of the current route
    const baseRoute = getBaseRoute(location.pathname);
    const newRoute = getLocalizedRoute(baseRoute, lang);
    
    // Handle dynamic routes (services/:id, articles/:slug)
    let finalRoute = newRoute;
    if (location.pathname.includes('/services/')) {
      const serviceId = location.pathname.split('/services/')[1] || location.pathname.split('/nl/services/')[1];
      if (serviceId) {
        finalRoute = lang === 'nl' ? `/nl/services/${serviceId}` : `/services/${serviceId}`;
      }
    } else if (location.pathname.includes('/articles/')) {
      const articleSlugOrInternal = location.pathname.split('/articles/')[1] || location.pathname.split('/nl/articles/')[1];
      if (articleSlugOrInternal) {
        // Articles can carry a different slug per language, so resolve the
        // internal slug first and then localize it for the target language.
        const internalSlug = getInternalArticleSlug(articleSlugOrInternal);
        const localizedSlug = getLocalizedArticleSlug(internalSlug, lang);
        finalRoute = lang === 'nl' ? `/nl/articles/${localizedSlug}` : `/articles/${localizedSlug}`;
      }
    }
    
    navigate(finalRoute, { replace: true });
  };

  const t = (key: string): any => {
    if (!translations) {
      console.warn('Translations not available for key:', key);
      return key;
    }
    
    try {
      const keys = key.split('.');
      let value: any = translations[language];
      
      // If language doesn't exist in translations, fallback to 'en'
      if (!value && language !== 'en') {
        value = translations['en'];
      }
      
      for (const k of keys) {
        if (value === null || value === undefined) {
          // Try fallback to English if current language doesn't have the key
          if (language !== 'en' && translations['en']) {
            let fallbackValue: any = translations['en'];
            for (const fallbackKey of keys) {
              if (fallbackValue === null || fallbackValue === undefined) {
                return key;
              }
              fallbackValue = fallbackValue[fallbackKey];
            }
            if (fallbackValue !== undefined && fallbackValue !== null) {
              return fallbackValue;
            }
          }
          return key;
        }
        value = value[k];
      }
      
      // Return the value as-is (could be string, array, object, etc.)
      return value !== undefined && value !== null ? value : key;
    } catch (error) {
      console.warn('Translation error for key:', key, error);
      return key;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}; 
